import ProfessionalIcon from '../../../components/common/ProfessionalIcon';
import AccountSecurityPanel from '../../../components/accountSecurity/AccountSecurityPanel';
import { saveOwnProfile, securityError } from '../../../services/accountSecurityService';
import { cookieStorage } from '../../../utils/cookieStorage';
import DocumentoClienteFields from '../../../components/common/DocumentoClienteFields';
// pages/Profile/ProfileLandingPage.tsx — Co Marca — estilo plataforma dashboard
import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import {
    Camera, Mail,
    User, Shield, CheckCircle2, Home, ChevronRight,
    Phone, CreditCard, Briefcase, CalendarCheck, Clock, XCircle,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { fetchCitas, Cita } from '../../../services/citasService';
import { getUserRingTier, isUserSuperAdmin, TIER_CONFIGS, UserRingTier, TierAvatar, getProfessionColor, getDynamicTierLabel, getDynamicTierSublabel } from '../../../utils/userTier';
import { fetchRamasProfesionales, RamaProfesional } from '../../../services/ramaProfesionalService';
import { getUserLevelLabel } from '../../../components/HeaderLanding/userPresentation';

const colors = {
    primary:         '#61257B',
    primaryDark:     '#451759',
    secondary:       '#C1348B',
    secondaryLight:  '#FFD97C',
    accent:          '#FFD97C',
    accentDark:      '#EA8406',
    moradoPrincipal: '#61257B',
    moradoVariante:  '#C1348B',
    naranjaIntenso:  '#EA8406',
    naranjaVariante: '#FFD97C',
};

const PROFESIONES_CATEGORIAS = [
    { id: 'ingenieria', label: 'Ingeniería & Arquitectura', icon: '🏗️', color: '#61257B', colorDark: '#C1348B' },
    { id: 'tecnologia', label: 'Tecnología & Software', icon: '💻', color: '#61257b', colorDark: '#004d7a' },
    { id: 'derecho',    label: 'Derecho & Legal',        icon: '⚖️', color: '#8f1f64', colorDark: '#60002e' },
    { id: 'marketing',  label: 'Marketing & Comunicación', icon: '📣', color: '#c1348b', colorDark: '#C1348B' },
    { id: 'finanzas',   label: 'Finanzas & Contabilidad', icon: '📊', color: '#EA8406', colorDark: '#b35900' },
    { id: 'salud',      label: 'Salud & Bienestar',      icon: '🧠', color: '#8b3fa8', colorDark: '#006b5a' },
];

const getCss = (isDark: boolean) => `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

@keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(97,37,123,.3); }
    50%     { box-shadow:0 0 0 5px rgba(97,37,123,0); }
}
@keyframes spin { to { transform:rotate(360deg); } }

.pl-page   { animation: fadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.pl-tab-btn:hover:not(.active) { color: ${isDark ? '#ffffff' : '#374151'} !important; background: ${isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc'} !important; }
.pl-input {
    width:100%; padding:10px 14px;
    border:1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.35)' : '#e5e7eb'};
    border-radius:10px;
    font-size:.9rem;
    color:${isDark ? '#ffffff' : '#374151'};
    font-family:'DM Sans',sans-serif;
    outline:none;
    background:${isDark ? 'rgba(15, 7, 28, 0.85)' : '#fafafa'};
    transition:border-color .2s, box-shadow .2s;
    box-sizing:border-box;
}
.pl-input:focus {
    border-color:${colors.primary} !important;
    box-shadow:0 0 0 3px rgba(97,37,123,.20) !important;
    background:${isDark ? 'rgba(20, 9, 38, 0.95)' : '#fff'} !important;
}
.pl-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
    color:#fff; border:none; border-radius:11px;
    padding:11px 26px; font-size:.88rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 18px rgba(97,37,123,.35);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.pl-btn-primary:hover { transform:translateY(-1px); box-shadow:0 8px 26px rgba(97,37,123,.45); }
.pl-btn-primary:disabled { background:#334155; color:#64748b; box-shadow:none; cursor:not-allowed; transform:none; }
.pl-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:${isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc'};
    border:1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0'};
    color:${isDark ? '#cbd5e1' : '#374151'};
    border-radius:11px;
    padding:10px 20px; font-size:.88rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.pl-btn-ghost:hover { background:${isDark ? 'rgba(255, 255, 255, 0.12)' : '#f1f5f9'}; color:${isDark ? '#ffffff' : '#0f172a'}; }
.pl-stat-card { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pl-stat-card:hover { transform:translateY(-3px); }
.pl-avatar-wrap:hover .pl-avatar-overlay { opacity:1 !important; }
@media (max-width:900px) {
    .pl-layout { flex-direction:column !important; }
    .pl-sidebar { width:100% !important; }
}
`;

// ── Stat types ────────────────────────────────────────────────
interface Stat { value: number; label: string; icon: React.ReactNode; color: string; }

const calcStatsPersonal = (citas: Cita[], userId: number): Stat[] => {
    const reg = citas.filter(
        (c) => (c as any).usuario_registrador === userId || (c as any).usuario_registrador_id === userId
    );
    return [
        { value: reg.length, label: 'Registradas', icon: <CalendarCheck size={16}/>, color: colors.primary },
        { value: reg.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color: '#10b981' },
        { value: reg.filter((c) => c.estado_ejecucion === 'Cancelada' || c.estado === 'Cancelada').length, label: 'Canceladas', icon: <XCircle size={16}/>, color: '#ef4444' },
    ];
};

const calcStatsCliente = (citas: Cita[], userId: number): Stat[] => {
    const own = citas.filter(
        (c) => Number(c.cliente) === userId || (c.clientes_asociados && c.clientes_asociados.includes(userId))
    );
    return [
        { value: own.length, label: 'Total Citas', icon: <CalendarCheck size={16}/>, color: colors.primary },
        { value: own.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color: '#10b981' },
        { value: own.filter((c) => c.estado === 'Pendiente' || c.estado_ejecucion === 'PorComenzar').length, label: 'Pendientes', icon: <Clock size={16}/>, color: colors.accentDark },
    ];
};

// ── MAIN ─────────────────────────────────────────────────────
const ProfileLandingPage = () => {
    const { user, setUser } = useAuth();
    const { isDark } = useTheme();

    const [activeTab, setActiveTab] = useState<'General' | 'Seguridad'>('General');

    // Imagen
    const [selectedImage, setSelectedImage] = useState<string>(user?.foto_url ?? '');
    const [selectedFile,  setSelectedFile]  = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Stats
    const blankStats: Stat[] = user?.perfil === 'personal'
        ? [
            { value:0, label:'Registradas', icon:<CalendarCheck size={16}/>, color:colors.secondary },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:colors.secondaryLight },
            { value:0, label:'Canceladas',   icon:<XCircle size={16}/>,      color:colors.primary },
        ]
        : [
            { value:0, label:'Total Citas',  icon:<CalendarCheck size={16}/>, color:colors.secondary },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:colors.secondaryLight },
            { value:0, label:'Pendientes',   icon:<Clock size={16}/>,         color:colors.accentDark },
        ];
    const [stats, setStats] = useState<Stat[]>(blankStats);
    const [statsLoading, setStatsLoading] = useState(true);

    // General form
    const [firstName,     setFirstName]     = useState(user?.first_name ?? '');
    const [lastName,      setLastName]      = useState(user?.last_name  ?? '');
    const [telefono,      setTelefono]      = useState(user?.telefono   ?? '');
    const [tipoDocumento, setTipoDocumento] = useState(user?.codigo_tipo_documento_identidad_sin ?? 1);
    const [complemento, setComplemento] = useState(user?.complemento ?? '');
    const [ciNit,         setCiNit]         = useState(user?.ci_nit     ?? '');
    const [cargo,         setCargo]         = useState(user?.cargo      ?? '');
    
    // Profesión para el Banco de Profesionales y Avatar
    const [profesionCategoria, setProfesionCategoria] = useState<string>(() => {
        if (user) {
            const userCat = cookieStorage.getItem(`user_categoria_${user.id}`);
            if (userCat) return userCat;
            const detected = getProfessionColor(user).categoria;
            if (detected) return detected;
        }
        return cookieStorage.getItem('user_categoria') || 'tecnologia';
    });
    const [profesionDetalle, setProfesionDetalle] = useState<string>(() => {
        if (user) {
            const userProf = cookieStorage.getItem(`user_profesion_${user.id}`);
            if (userProf) return userProf;
        }
        return cookieStorage.getItem('user_profesion') || user?.especialidad || user?.cargo || '';
    });

    const [ramas, setRamas] = useState<RamaProfesional[]>([]);

    useEffect(() => {
        let isMounted = true;
        fetchRamasProfesionales().then(res => {
            if (isMounted && res && res.length > 0) {
                setRamas(res);
            }
        }).catch(err => console.error('Error fetching ramas:', err));
        return () => { isMounted = false; };
    }, []);

    const categoriasList = ramas.length > 0
        ? ramas.map(r => ({
            id: r.codigo,
            label: r.nombre,
            icon: r.icono || '💼',
            color: r.color || '#61257B',
            colorDark: r.color_dark || '#8f1f64',
            subramas: r.subramas || []
        }))
        : PROFESIONES_CATEGORIAS.map(c => ({ ...c, subramas: [] as string[] }));

    useEffect(() => {
        if (user) {
            setFirstName(user.first_name || '');
            setLastName(user.last_name || '');
            setTelefono(user.telefono || '');
            setCiNit(user.ci_nit || '');
            setTipoDocumento(user.codigo_tipo_documento_identidad_sin ?? 1);
            setComplemento(user.complemento || '');
            setCargo(user.cargo || '');

            const userCat = cookieStorage.getItem(`user_categoria_${user.id}`);
            if (userCat) {
                setProfesionCategoria(userCat);
            } else {
                const detected = getProfessionColor(user).categoria;
                if (detected) setProfesionCategoria(detected);
            }

            const userProf = cookieStorage.getItem(`user_profesion_${user.id}`);
            if (userProf) {
                setProfesionDetalle(userProf);
            } else if (user.especialidad || user.cargo) {
                setProfesionDetalle(user.especialidad || user.cargo || "");
            }
        }
    }, [user]);

    // Nivel de Aro y Membresía Co Marca: 100% automático según el plan adquirido
    const [, setTierTick] = useState(0);
    useEffect(() => {
        const onStorage = () => setTierTick(t => t + 1);
        window.addEventListener('cookie-preferences-changed', onStorage);
        return () => window.removeEventListener('cookie-preferences-changed', onStorage);
    }, []);

    const userTier = getUserRingTier(user);

    const handleSelectTier = (tier: UserRingTier) => {
        if (!isUserSuperAdmin(user)) return;
        cookieStorage.setItem('user_tier_simulator', tier);
        window.dispatchEvent(new Event('cookie-preferences-changed'));
    };

    const [savingGeneral, setSavingGeneral] = useState(false);
    const [generalMsg,    setGeneralMsg]    = useState<{ type:'success'|'error'; text:string }|null>(null);

    useEffect(() => {
        if (user?.foto_url && !selectedFile) setSelectedImage(user.foto_url);
    }, [user?.foto_url, selectedFile]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            setStatsLoading(true);
            try {
                const citas = await fetchCitas();
                setStats(user.perfil === 'personal'
                    ? calcStatsPersonal(citas, user.id)
                    : calcStatsCliente(citas, user.id));
            } catch { /* ignore */ }
            finally { setStatsLoading(false); }
        })();
    }, [user]);

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024 || !['image/jpeg','image/png','image/webp'].includes(file.type)) {
            setGeneralMsg({type:'error',text:'Usa una foto JPG, PNG o WebP de hasta 5 MB.'});
            e.target.value = ''; return;
        }
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => { if (ev.target?.result) setSelectedImage(ev.target.result as string); };
        reader.readAsDataURL(file);
    };

    const handleSaveGeneral = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        setSavingGeneral(true);
        setGeneralMsg(null);
        try {
            const finalCargo = profesionDetalle || cargo;
            const updated = await saveOwnProfile({first_name:firstName, last_name:lastName, telefono, ci_nit:ciNit, codigo_tipo_documento_identidad_sin:tipoDocumento, complemento, cargo:finalCargo, especialidad:finalCargo, foto:selectedFile});
            const newFotoUrl = updated.foto_url;
            cookieStorage.setItem(`user_profesion_${user.id}`, finalCargo);
            cookieStorage.setItem('user_profesion', finalCargo);
            cookieStorage.setItem(`user_categoria_${user.id}`, profesionCategoria);
            cookieStorage.setItem('user_categoria', profesionCategoria);
            window.dispatchEvent(new Event('cookie-preferences-changed'));
            setUser(updated);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (newFotoUrl) setSelectedImage(newFotoUrl);
            setGeneralMsg({ type:'success', text:'Perfil y profesión actualizados correctamente.' });
        } catch (err) {
            setGeneralMsg({ type:'error', text:securityError(err) });
        } finally { setSavingGeneral(false); }
    };

    const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase();

    return (
        <div style={{ fontFamily:"'DM Sans',sans-serif", background:'transparent', minHeight:'100vh', position: 'relative', zIndex: 1 }}>
            <style>{getCss(isDark)}</style>

            {/* ── PAGE HEADER ── */}
            <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px) 0' }}>
                <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color: isDark ? '#94a3b8' : '#94a3b8', marginBottom:'10px' }}>
                    <a href="/midashboard" style={{ color: isDark ? '#94a3b8' : '#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px' }}
                        onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                        onMouseLeave={e=>e.currentTarget.style.color=isDark ? '#94a3b8' : '#94a3b8'}>
                        <Home size={11}/> Dashboard
                    </a>
                    <ChevronRight size={10} color={isDark ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}/>
                    <span style={{ color:colors.primary, fontWeight:'700' }}>Mi Perfil</span>
                </nav>

                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'12px', marginBottom:'6px' }}>
                    <h1 style={{ fontSize:'clamp(1.6rem,3vw,2.1rem)', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing:'-.025em', lineHeight:1.1 }}>
                        Mi <span style={{ color:colors.primary }}>Perfil</span>
                    </h1>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', background: isDark ? 'rgba(97, 37, 123, 0.2)' : `${colors.primary}10`, border:`1.5px solid ${isDark ? 'rgba(193, 52, 139, 0.35)' : `${colors.primary}20`}`, borderRadius:'100px', padding:'5px 14px', fontSize:'.72rem', fontWeight:'700', color: isDark ? '#ffd97c' : colors.primaryDark }}>
                        <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:colors.primary, animation:'pulseD 2s infinite', display:'inline-block' }}/>
                        {getUserLevelLabel(user)}
                    </span>
                </div>
                <p style={{ fontSize:'.85rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight:'500', marginBottom:'10px' }}>
                    Administra tu información personal y seguridad de cuenta.
                </p>
                <div style={{ display:'flex', gap:'5px', marginBottom:'28px' }}>
                    {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                        <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:c }}/>
                    ))}
                </div>
            </div>

            {/* ── LAYOUT ── */}
            <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'0 clamp(20px,4vw,40px) 60px' }}>
                <div className="pl-layout pl-page" style={{ display:'flex', gap:'24px', alignItems:'flex-start' }}>

                    {/* ══ SIDEBAR ══ */}
                    <div className="pl-sidebar" style={{ width:'260px', flexShrink:0, display:'flex', flexDirection:'column', gap:'16px' }}>

                        {/* Avatar card */}
                        <div style={{
                            background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                            borderRadius:'20px',
                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                            padding:'28px 20px',
                            boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,.05)',
                            backdropFilter: 'blur(14px)',
                            textAlign:'center'
                        }}>
                            {/* Barra colores */}
                            <div style={{ display:'flex', gap:'4px', justifyContent:'center', marginBottom:'20px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'20px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>

                            {/* Avatar con Aro según Tier de Usuario */}
                            {(() => {
                                const activeCat = categoriasList.find(c => c.id === profesionCategoria) || categoriasList[0];
                                const tierConfig = TIER_CONFIGS[userTier] || TIER_CONFIGS.comun;

                                return (
                                    <>
                                        <div style={{ position:'relative', display:'inline-block', marginBottom:'14px' }} className="pl-avatar-wrap">
                                            <TierAvatar
                                                tier={userTier}
                                                size={92}
                                                ringThickness={3.5}
                                                initial={initials || 'C'}
                                                bgColor={activeCat.color}
                                                fotoUrl={selectedImage || user?.foto_url}
                                                isOpenOrHovered={true}
                                            />
                                            <div className="pl-avatar-overlay" onClick={()=>fileInputRef.current?.click()} style={{
                                                position:'absolute', inset:'6px', borderRadius:'50%', background:'rgba(0,0,0,.5)',
                                                display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
                                                opacity:0, transition:'opacity .2s',
                                            }}>
                                                <Camera size={20} color="#fff"/>
                                            </div>
                                            <input type="file" ref={fileInputRef} onChange={handleImageChange} style={{ display:'none' }} accept="image/jpeg,image/png,image/webp"/>
                                        </div>

                                        {selectedFile && (
                                            <p style={{ fontSize:'.68rem', color:colors.accentDark, fontWeight:'600', marginBottom:'8px' }}>
                                                Imagen seleccionada — guarda para confirmar
                                            </p>
                                        )}

                                        <div style={{ fontWeight:'900', fontSize:'1rem', color: isDark ? '#ffffff' : '#0f172a', letterSpacing:'-.01em' }}>
                                            {user?.first_name} {user?.last_name}
                                        </div>

                                        {/* Badge del Aro / Membresía Co Marca */}
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                                            background: tierConfig.badgeBg, border: tierConfig.badgeBorder,
                                            borderRadius: '100px', padding: '3px 12px', marginTop: '6px',
                                            fontSize: '.72rem', fontWeight: '800', color: tierConfig.badgeColor,
                                        }}>
                                            <Shield size={14} style={{display:"inline-block",verticalAlign:"middle"}}/> {getDynamicTierLabel(user, tierConfig)}
                                        </div>

                                        {/* Pill de Profesión */}
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                                            background: `${activeCat.color}14`, border: `1.5px solid ${activeCat.color}30`,
                                            borderRadius: '100px', padding: '3px 12px', marginTop: '6px',
                                            fontSize: '.72rem', fontWeight: '700', color: activeCat.color,
                                        }}>
                                            <ProfessionalIcon category={activeCat.id} size={16}/> {profesionDetalle || activeCat.label}
                                        </div>
                                    </>
                                );
                            })()}

                            {/* Email pill */}
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', justifyContent:'center', marginTop:'12px', background: isDark ? 'rgba(97, 37, 123, 0.2)' : `${colors.primary}0a`, border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : `1.5px solid ${colors.primary}15`, borderRadius:'100px', padding:'5px 12px' }}>
                                <Mail size={11} color={colors.secondary}/>
                                <span style={{ fontSize:'.68rem', color: isDark ? '#ffd97c' : colors.secondary, fontWeight:'600', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</span>
                            </div>
                        </div>

                        {/* Stats card */}
                        <div style={{
                            background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                            borderRadius:'20px',
                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                            padding:'20px',
                            boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,.05)',
                            backdropFilter: 'blur(14px)'
                        }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'16px' }}>
                                <div style={{ height:'2px', width:'20px', background:colors.primary, borderRadius:'2px' }}/>
                                <span style={{ fontSize:'.65rem', fontWeight:'800', color:colors.primary, textTransform:'uppercase', letterSpacing:'.1em' }}>Estadísticas</span>
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                                {stats.map(s => (
                                    <div key={s.label} className="pl-stat-card" style={{
                                        display:'flex', alignItems:'center', gap:'12px',
                                        background: isDark ? 'rgba(255, 255, 255, 0.04)' : `${s.color}06`,
                                        borderRadius:'12px', padding:'10px 14px',
                                        border: isDark ? `1.5px solid ${s.color}35` : `1.5px solid ${s.color}14`,
                                    }}>
                                        <div style={{ width:'32px', height:'32px', borderRadius:'9px', background:`${s.color}14`, color:s.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                            {s.icon}
                                        </div>
                                        <div>
                                            <div style={{ fontSize:'1.1rem', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', lineHeight:1 }}>
                                                {statsLoading ? '—' : s.value}
                                            </div>
                                            <div style={{ fontSize:'.68rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight:'600', marginTop:'1px' }}>{s.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Declaración Jurada card — solo clientes */}
                        {user?.perfil === 'cliente' && (
                            <div style={{
                                background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                                borderRadius:'20px',
                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                                padding:'18px 20px',
                                boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,.05)',
                                backdropFilter: 'blur(14px)'
                            }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'12px' }}>
                                    <div style={{ height:'2px', width:'20px', background: (user as any).declaracion_jurada_firmada ? '#16a34a' : colors.primary, borderRadius:'2px' }}/>
                                    <span style={{ fontSize:'.65rem', fontWeight:'800', color: (user as any).declaracion_jurada_firmada ? '#16a34a' : colors.primary, textTransform:'uppercase', letterSpacing:'.1em' }}>
                                        Dec. Jurada
                                    </span>
                                </div>
                                {(user as any).declaracion_jurada_firmada ? (
                                    <div style={{
                                        background: isDark ? 'rgba(22, 163, 74, 0.15)' : '#dcfce7',
                                        border: isDark ? '1.5px solid rgba(34, 197, 94, 0.35)' : '1.5px solid #bbf7d0',
                                        borderRadius: '12px', padding: '10px 12px'
                                    }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                            <div style={{ width:'28px', height:'28px', borderRadius:'8px', background: isDark ? 'rgba(34, 197, 94, 0.25)' : '#16a34a20', color: isDark ? '#4ade80' : '#16a34a', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                <CheckCircle2 size={15}/>
                                            </div>
                                            <div>
                                                <div style={{ fontSize:'.78rem', fontWeight:'800', color: isDark ? '#4ade80' : '#15803d', lineHeight:1 }}>Acreditada</div>
                                                <div style={{ fontSize:'.65rem', color: isDark ? '#bbf7d0' : '#166534', fontWeight:'600', marginTop:'2px' }}>
                                                    {(user as any).fecha_declaracion_jurada
                                                        ? `Firmada el ${new Date((user as any).fecha_declaracion_jurada).toLocaleDateString('es-ES')}`
                                                        : 'Firma registrada'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{
                                        background: isDark ? 'rgba(217, 119, 6, 0.15)' : '#fef3c7',
                                        border: isDark ? '1.5px solid rgba(245, 158, 11, 0.35)' : '1.5px solid #fde68a',
                                        borderRadius: '12px', padding: '10px 12px'
                                    }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                            <div style={{ width:'28px', height:'28px', borderRadius:'8px', background: isDark ? 'rgba(245, 158, 11, 0.25)' : '#d9770620', color: isDark ? '#fbbf24' : '#d97706', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1rem' }}>
                                                ⚠
                                            </div>
                                            <div>
                                                <div style={{ fontSize:'.78rem', fontWeight:'800', color: isDark ? '#fbbf24' : '#92400e', lineHeight:1 }}>Pendiente</div>
                                                <div style={{ fontSize:'.65rem', color: isDark ? '#fde68a' : '#78350f', fontWeight:'600', marginTop:'2px' }}>Contacte a recepción</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>

                    {/* ══ MAIN PANEL ══ */}
                    <div style={{ flex:1, minWidth:0 }}>
                        <div style={{
                            background: isDark ? 'rgba(22, 10, 43, 0.88)' : '#fff',
                            borderRadius:'20px',
                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                            boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,.05)',
                            backdropFilter: 'blur(14px)',
                            overflow:'hidden'
                        }}>

                            {/* Tabs */}
                            <div style={{ display:'flex', borderBottom: isDark ? '1.5px solid rgba(193, 52, 139, 0.2)' : '1.5px solid #f1f5f9', padding:'0 24px' }}>
                                {(['General','Seguridad'] as const).map(tab => {
                                    const active = activeTab === tab;
                                    return (
                                        <button key={tab} onClick={()=>setActiveTab(tab)} className={`pl-tab-btn${active?' active':''}`} style={{
                                            display:'inline-flex', alignItems:'center', gap:'7px',
                                            padding:'16px 4px', marginRight:'24px',
                                            background:'transparent', border:'none', cursor:'pointer',
                                            fontFamily:"'DM Sans',sans-serif",
                                            fontSize:'.88rem', fontWeight: active?'800':'600',
                                            color: active ? colors.primary : (isDark ? '#cbd5e1' : '#94a3b8'),
                                            borderBottom: active ? `2px solid ${colors.primary}` : '2px solid transparent',
                                            marginBottom:'-1.5px', transition:'all .2s',
                                        }}>
                                            {tab === 'General' ? <User size={14}/> : <Shield size={14}/>}
                                            {tab}
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={{ padding:'28px 28px 32px' }}>

                                {/* ════ TAB GENERAL ════ */}
                                {activeTab === 'General' && (
                                    <form onSubmit={handleSaveGeneral} style={{ animation:'fadeUp .35s ease both' }}>
                                        <div style={{ marginBottom:'24px' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                                                <div style={{ width:'28px', height:'28px', borderRadius:'8px', background:`${colors.primary}12`, color:colors.primary, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <User size={14}/>
                                                </div>
                                                <h2 style={{ fontSize:'1rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a' }}>Información Personal</h2>
                                            </div>
                                            <p style={{ fontSize:'.8rem', color: isDark ? '#cbd5e1' : '#94a3b8' }}>Actualiza tu nombre y datos de contacto.</p>
                                        </div>

                                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'24px' }}>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'6px' }}>
                                                    <User size={11} color={colors.primary}/> Nombre
                                                </label>
                                                <input type="text" value={firstName} onChange={e=>setFirstName(e.target.value)} className="pl-input" placeholder="Tu nombre"/>
                                            </div>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'6px' }}>
                                                    <User size={11} color={colors.primary}/> Apellido
                                                </label>
                                                <input type="text" value={lastName} onChange={e=>setLastName(e.target.value)} className="pl-input" placeholder="Tu apellido"/>
                                            </div>
                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'6px' }}>
                                                    <Phone size={11} color={colors.secondary}/> Teléfono
                                                </label>
                                                <input type="text" value={telefono} onChange={e=>setTelefono(e.target.value)} className="pl-input" placeholder="+591 7XX XXXXX"/>
                                            </div>

                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'6px' }}>
                                                    <CreditCard size={11} color={colors.secondary}/> CI / NIT
                                                </label>
                                                <input type="text" value={ciNit} onChange={e=>setCiNit(e.target.value)} className="pl-input" placeholder="12345678"/>
                                            </div>
                                        </div>

                                        {user?.perfil === 'cliente' && <DocumentoClienteFields containerStyle={{ gridColumn: '1 / -1', gap: 16 }} labelClassName="" labelStyle={{ fontSize: '0.78rem', fontWeight: 700, color: isDark ? '#cbd5e1' : '#374151' }} codigo={tipoDocumento} complemento={complemento} onChange={(codigo, complemento) => { setTipoDocumento(codigo); setComplemento(complemento); }} />}
                                        {/* ── SECCIÓN DE PROFESIÓN & BANCO DE PROFESIONALES ── */}
                                        <div style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.75)' : '#f8fafc',
                                            borderRadius:'16px',
                                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #e2e8f0',
                                            padding:'18px 20px',
                                            marginBottom:'24px'
                                        }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                                                <Briefcase size={16} color={colors.moradoVariante}/>
                                                <h3 style={{ fontSize:'.92rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', margin:0 }}>
                                                    Profesión & Banco de Profesionales
                                                </h3>
                                            </div>
                                            <p style={{ fontSize:'.75rem', color: isDark ? '#cbd5e1' : '#64748b', marginBottom:'14px', lineHeight:'1.5' }}>
                                                Selecciona tu área profesional. Esto definirá el color y la estela multicolor de tu avatar en la plataforma, y te posicionará en el Banco de Profesionales de Co Marca.
                                            </p>

                                            {/* Selector de categorías visual */}
                                            <label style={{ fontSize:'.74rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'8px', display:'block' }}>
                                                Área Profesional (Rama Principal)
                                            </label>
                                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:'8px', marginBottom:'14px' }}>
                                                {categoriasList.map(cat => {
                                                    const isSelected = profesionCategoria === cat.id;
                                                    return (
                                                        <button
                                                            key={cat.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setProfesionCategoria(cat.id);
                                                                if (!profesionDetalle || categoriasList.some(c => c.label === profesionDetalle)) {
                                                                    setProfesionDetalle(cat.label);
                                                                }
                                                                if (user) {
                                                                    cookieStorage.setItem(`user_categoria_${user.id}`, cat.id);
                                                                    cookieStorage.setItem('user_categoria', cat.id);
                                                                    window.dispatchEvent(new Event('cookie-preferences-changed'));
                                                                }
                                                            }}
                                                            style={{
                                                                background: isSelected ? `${cat.color}25` : (isDark ? 'rgba(25, 12, 45, 0.6)' : '#fff'),
                                                                border: isSelected ? `2px solid ${cat.color}` : (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #e2e8f0'),
                                                                borderRadius:'10px',
                                                                padding:'10px 8px',
                                                                cursor:'pointer',
                                                                textAlign:'center',
                                                                transition:'all .18s ease',
                                                                boxShadow: isSelected ? `0 2px 12px ${cat.color}35` : 'none',
                                                            }}
                                                        >
                                                            <div style={{ fontSize:'1.2rem', marginBottom:'3px' }}><ProfessionalIcon category={cat.id}/></div>
                                                            <div style={{ fontSize:'.72rem', fontWeight: isSelected ? '800' : '600', color: isSelected ? cat.color : (isDark ? '#e2e8f0' : '#334155'), lineHeight:'1.2' }}>
                                                                {cat.label}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Sub-ramas sugeridas si existen */}
                                            {(() => {
                                                const currentBranch = categoriasList.find(c => c.id === profesionCategoria) || categoriasList[0];
                                                if (!currentBranch || !currentBranch.subramas || currentBranch.subramas.length === 0) return null;
                                                return (
                                                    <div style={{
                                                        marginBottom: '14px',
                                                        background: isDark ? 'rgba(25, 12, 45, 0.7)' : '#fff',
                                                        borderRadius: '12px',
                                                        padding: '12px 14px',
                                                        border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #e2e8f0'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                            <label style={{ fontSize: '.74rem', fontWeight: '800', color: isDark ? '#e2e8f0' : '#334155', margin: 0 }}>
                                                                Especialidades sugeridas en <span style={{ color: currentBranch.color }}>{currentBranch.label}</span>:
                                                            </label>
                                                            <span style={{ fontSize: '.68rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>Clic para autocompletar</span>
                                                        </div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {currentBranch.subramas.map(sub => {
                                                                const isSelected = profesionDetalle.trim().toLowerCase() === sub.trim().toLowerCase();
                                                                return (
                                                                    <button
                                                                        key={sub}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setProfesionDetalle(sub);
                                                                        }}
                                                                        style={{
                                                                            padding: '5px 12px',
                                                                            borderRadius: '20px',
                                                                            border: isSelected ? `1.5px solid ${currentBranch.color}` : (isDark ? '1.5px solid rgba(255, 255, 255, 0.1)' : '1.5px solid #cbd5e1'),
                                                                            background: isSelected ? `${currentBranch.color}25` : (isDark ? 'rgba(15, 7, 28, 0.7)' : '#f8fafc'),
                                                                            color: isSelected ? currentBranch.color : (isDark ? '#e2e8f0' : '#334155'),
                                                                            fontWeight: isSelected ? '800' : '600',
                                                                            fontSize: '.74rem',
                                                                            cursor: 'pointer',
                                                                            transition: 'all .15s ease',
                                                                            boxShadow: isSelected ? `0 2px 8px ${currentBranch.color}25` : 'none',
                                                                        }}
                                                                    >
                                                                        {sub}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            <div>
                                                <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'.78rem', fontWeight:'700', color: isDark ? '#cbd5e1' : '#374151', marginBottom:'6px' }}>
                                                    <span>Especialidad o Título Específico</span>
                                                    <span style={{ fontSize:'.68rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight:'500' }}>Personalizable libremente</span>
                                                </label>
                                                <input 
                                                    type="text" 
                                                    value={profesionDetalle} 
                                                    onChange={e => setProfesionDetalle(e.target.value)} 
                                                    className="pl-input" 
                                                    placeholder="Ej: Ingeniero Civil, Arquitecto Urbanista, Desarrollador Fullstack..."
                                                />
                                                <p style={{ fontSize:'.70rem', color: isDark ? '#94a3b8' : '#64748b', marginTop:'4px', marginBottom:0 }}>
                                                    Tu rama principal te ubicará en la categoría correcta del Banco de Profesionales, manteniendo tu especialidad exacta visible.
                                                </p>
                                            </div>
                                        </div>

                                        {/* ── SECCIÓN DE NIVEL DE ARO & MEMBRESÍA CO MARCA (SOLO SUPERADMIN) ── */}
                                        {isUserSuperAdmin(user) ? (
                                            <div style={{
                                                background: isDark ? 'rgba(15, 7, 28, 0.75)' : '#fff',
                                                borderRadius:'16px',
                                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #e2e8f0',
                                                padding:'18px 20px',
                                                marginBottom:'24px'
                                            }}>
                                                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px', flexWrap:'wrap', gap:'8px' }}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                        <Shield size={16} color={colors.primary}/>
                                                        <h3 style={{ fontSize:'.92rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', margin:0 }}>
                                                            Nivel de Membresía & Aro Co Marca
                                                        </h3>
                                                        <span style={{ fontSize:'.65rem', fontWeight:'800', background:`${colors.primary}15`, color:colors.primary, borderRadius:'6px', padding:'2px 8px' }}>
                                                            Modo SuperAdmin (Simulador de Prueba)
                                                        </span>
                                                    </div>
                                                    <span style={{ fontSize:'.7rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight:'600' }}>
                                                        Aro visual en cabecera y perfiles
                                                    </span>
                                                </div>
                                                <p style={{ fontSize:'.75rem', color: isDark ? '#cbd5e1' : '#64748b', marginBottom:'14px', lineHeight:'1.5' }}>
                                                    Como Super Admin, puedes probar visualmente cualquiera de los 4 aros en tiempo real. Para los demás usuarios de la plataforma, el aro se define <strong>100% automáticamente según su plan adquirido</strong>:
                                                </p>

                                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'10px' }}>
                                                    {(['superadmin', 'personal', 'domiciliado', 'comun'] as UserRingTier[]).map((t) => {
                                                        const cfg = TIER_CONFIGS[t];
                                                        const isSelected = userTier === t;
                                                        return (
                                                            <button
                                                                key={t}
                                                                type="button"
                                                                onClick={() => handleSelectTier(t)}
                                                                style={{
                                                                    background: isSelected ? `${cfg.badgeColor}15` : (isDark ? 'rgba(25, 12, 45, 0.6)' : '#fafafa'),
                                                                    border: isSelected ? `2px solid ${cfg.badgeColor}` : (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #e2e8f0'),
                                                                    borderRadius:'12px',
                                                                    padding:'12px',
                                                                    cursor:'pointer',
                                                                    textAlign:'left',
                                                                    display:'flex',
                                                                    alignItems:'center',
                                                                    gap:'12px',
                                                                    transition:'all .2s ease',
                                                                    boxShadow: isSelected ? `0 4px 14px ${cfg.badgeColor}25` : 'none',
                                                                }}
                                                            >
                                                                <TierAvatar
                                                                    tier={t}
                                                                    size={42}
                                                                    ringThickness={2.5}
                                                                    initial={initials || 'C'}
                                                                    bgColor={cfg.badgeColor}
                                                                    fotoUrl={selectedImage || user?.foto_url}
                                                                    isOpenOrHovered={isSelected}
                                                                />
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontSize:'.8rem', fontWeight:'800', color: isSelected ? cfg.badgeColor : (isDark ? '#ffffff' : '#1e293b'), display:'flex', alignItems:'center', gap:'4px' }}>
                                                                        <Shield size={14} style={{display:"inline-block",verticalAlign:"middle"}}/> {getDynamicTierLabel(user, cfg)}
                                                                    </div>
                                                                    <div style={{ fontSize:'.68rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight:'500', marginTop:'2px' }}>
                                                                        {getDynamicTierSublabel(user, cfg)}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                background: isDark ? 'rgba(15, 7, 28, 0.75)' : '#f8fafc',
                                                borderRadius:'16px',
                                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #e2e8f0',
                                                padding:'16px 20px',
                                                marginBottom:'24px',
                                                display:'flex',
                                                alignItems:'center',
                                                justifyContent:'space-between',
                                                flexWrap:'wrap',
                                                gap:'12px'
                                            }}>
                                                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                                                    <TierAvatar
                                                        tier={userTier}
                                                        size={42}
                                                        ringThickness={2.5}
                                                        initial={initials || 'C'}
                                                        bgColor={(categoriasList.find(c => c.id === profesionCategoria) || categoriasList[0]).color}
                                                        fotoUrl={selectedImage || user?.foto_url}
                                                        isOpenOrHovered={true}
                                                    />
                                                    <div>
                                                        <div style={{ fontSize:'.84rem', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', display:'flex', alignItems:'center', gap:'6px' }}>
                                                            <span>Membresía Oficial:</span>
                                                            <span style={{ color: TIER_CONFIGS[userTier]?.badgeColor, display:'inline-flex', alignItems:'center', gap:'4px' }}>
                                                                <Shield size={14} style={{display:"inline-block",verticalAlign:"middle"}}/> {getDynamicTierLabel(user, TIER_CONFIGS[userTier])}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize:'.72rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight:'500', marginTop:'2px' }}>
                                                            {getDynamicTierSublabel(user, TIER_CONFIGS[userTier])} · Aro asignado automáticamente según el plan activo
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: TIER_CONFIGS[userTier]?.badgeBg,
                                                    border: TIER_CONFIGS[userTier]?.badgeBorder,
                                                    borderRadius:'100px',
                                                    padding:'4px 12px',
                                                    fontSize:'.72rem',
                                                    fontWeight:'800',
                                                    color: TIER_CONFIGS[userTier]?.badgeColor
                                                }}>
                                                    Plan Activo
                                                </div>
                                            </div>
                                        )}

                                        {generalMsg && (
                                            <div style={{
                                                display:'flex', alignItems:'center', gap:'8px',
                                                background: generalMsg.type==='success' ? (isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5') : (isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2'),
                                                border:`1.5px solid ${generalMsg.type==='success' ? '#10b981' : '#ef4444'}`,
                                                borderRadius:'10px', padding:'10px 14px', marginBottom:'18px',
                                            }}>
                                                {generalMsg.type==='success' ? <CheckCircle2 size={14} color="#10b981"/> : <XCircle size={14} color="#ef4444"/>}
                                                <span style={{ fontSize:'.8rem', fontWeight:'600', color: generalMsg.type==='success' ? '#10b981' : '#ef4444' }}>
                                                    {generalMsg.text}
                                                </span>
                                            </div>
                                        )}

                                        <button type="submit" disabled={savingGeneral} className="pl-btn-primary">
                                            {savingGeneral
                                                ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block'}}/>Guardando...</>
                                                : <><CheckCircle2 size={14}/> Guardar cambios</>
                                            }
                                        </button>
                                    </form>
                                )}

                                {/* ════ TAB SEGURIDAD ════ */}
                                {activeTab === 'Seguridad' && <AccountSecurityPanel />}

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ProfileLandingPage;

